import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18nService } from '@app/i18n/services';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { FormsService } from '@services/forms';
import { ProgressIndicatorService } from '@services/progress';
import { IManagedUser, UsersService } from '@services/users';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, finalize, first, map, switchMap, tap } from 'rxjs/operators';

import { DialogService } from '../../../../../../services/dialog';
import { FormEditorInputType } from '../../../../../../shared/components';
import { IUsersEditEmailDialogData } from './users-edit-email.models';

@Component({
	selector: 'app-users-edit-email',
	templateUrl: './users-edit-email.dialog.html',
	styleUrls: ['./users-edit-email.dialog.scss']
})
export class UsersEditEmailDialogComponent implements OnInit {
	private dialogRefClose: () => void;
	public validatingInput = false;
	public formEditorInputType = FormEditorInputType;
	public formInputs = this.formBuilder.group({
		email: ['', [this.formsService.emailValidator()], [this.usersEmailExists()]]
	});

	constructor(
		private i18nService: I18nService,
		private feedbackService: FeedbackService,
		private usersService: UsersService,
		private progressIndicatorService: ProgressIndicatorService,
		private formsService: FormsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: IUsersEditEmailDialogData,
		private dialogRef: MatDialogRef<UsersEditEmailDialogComponent>,
		private formBuilder: FormBuilder,
		private dialogService: DialogService
	) {
		// Store original close function and replace with unsaved version, remember to restore on use
		this.dialogRefClose = this.dialogService.interceptClose(this.dialogRef, this, this.closeDialog);
	}

	public ngOnInit(): void {
		this.formInputs.patchValue({
			email: this.dialogData.user.email
		});
	}

	public onFormSubmit(): void {
		if (!this.hasChanges()) {
			this.dialogRefClose();
		} else {
			this.feedbackService.confirm({
				title: FeedbackTitle.warning,
				message: this.i18nService.get(
					"Warning! Changing the user's email address automatically validates the provided address. If you have entered an incorrect address you could lock the user out of their account. Are you sure you want to continue?",
					'app.prompt.users.edit-user-email.confirm-message'
				),
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) this.updateUserEmail();
				}
			});
		}
	}

	private hasChanges(): boolean {
		return this.formInputs.controls.email.value.trim().toLowerCase() !== this.dialogData.user.email;
	}

	private closeDialog(): void {
		if (this.hasChanges()) {
			this.feedbackService.confirm({
				title: FeedbackTitle.warning,
				message: this.i18nService.get(
					'You have unsaved changes that will be lost. Are you sure you want to continue?',
					'app.prompt.pending-changes-confirm-message'
				),
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) this.dialogRefClose();
				}
			});
		} else {
			this.dialogRefClose();
		}
	}

	private usersEmailExists(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<{ [key: string]: any } | null> => {
			this.validatingInput = false;
			control.setErrors(null);

			if (!control.value || control.value?.trim() === this.dialogData.user.email) {
				return of(null);
			}

			return control.valueChanges.pipe(
				tap(() => {
					this.validatingInput = true;
					control.setErrors({ checkingEmail: true });
				}),
				debounceTime(800),
				switchMap(() => {
					return this.usersService
						.usersMigrateGigya(control.value)
						.pipe(
							tap(() => {
								this.validatingInput = false;
								this.formInputs.markAllAsTouched();
							}),
							map((exists: boolean) => {
								const value = control.value ? control.value.trim() : '';
								const emailExists = exists && value !== this.dialogData.user.email;
								const error = emailExists ? { emailExists: true } : null;

								control.setErrors(error);

								return error;
							}),
							catchError(() => {
								this.validatingInput = false;
								this.formInputs.markAllAsTouched();
								return of(null);
							})
						);
				}),
				first(),
				finalize(() => (this.validatingInput = false))
			);
		};
	}

	private updateUserEmail(): void {
//		this.progressIndicatorService.spinner(true);

//		this.usersService
//			.updateUserEmail(
//				this.dialogData.user.id,
//				this.dialogData.user.identities['COGNITO'],
//				this.dialogData.clusterId,
//				this.dialogData.appKeyClientId,
//				this.formInputs.controls.email.value
//			)
//			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
//			.subscribe(
//				(res: IManagedUser) => {
//					// TODO what is return data and how do we handle?
//					// this.dialogData.user = res;
//
//					this.dialogData.callback(this.dialogData.user, true);
//					this.feedbackService.notification({
//						msg: this.i18nService.get('User email updated.', 'app.descriptor.users.updated-user-email'),
//						config: { duration: 4000 }
//					});
//					this.dialogRefClose();
//				},
//				(error: any) => this.feedbackService.apiError(error)
//			);
	}
}
